import { Inject, Injectable, OnDestroy } from '@angular/core';
import { NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';
import { NewRelicEventsService } from '@app/services/new-relic-events.service';
import { WINDOW_REF } from '@app/services/offline-event-queue.service';
import { OfflineConnectivityModalService } from '@app/services/offline-connectivity-modal.service';

/**
 * Detecta perdida de conexion a internet
 * muestra un modal a pantalla completa y envia a New Relic
 * la duración de la interrupcion al recuperar la red.
 *
 * Modal operations are now delegated to OfflineConnectivityModalService
 * which owns the single modal instance and handles priority logic.
 */
@Injectable({
  providedIn: 'root',
})
export class ConnectivityMonitoringService implements OnDestroy {
  private readonly offlineBodyClass = 'offline-connectivity-active';
  private offlineStartedAt: number | null = null;
  private readonly checkIntervalMs = 2_000;
  private connectivityCheckIntervalId: number | null = null;
  private lastKnownOnlineState: boolean;

  private readonly onOffline = () => this.handleOffline();
  private readonly onOnline = () => this.handleOnline();

  constructor(
    @Inject(WINDOW_REF) private readonly win: Window,
    private readonly modalService: OfflineConnectivityModalService,
    private readonly newRelicEvents: NewRelicEventsService
  ) {
    this.lastKnownOnlineState = this.win.navigator.onLine;
    this.win.addEventListener('offline', this.onOffline);
    this.win.addEventListener('online', this.onOnline);
    this.startConnectivityPolling();
    if (!this.win.navigator.onLine) {
      this.handleOffline();
    }
  }

  ngOnDestroy(): void {
    this.win.removeEventListener('offline', this.onOffline);
    this.win.removeEventListener('online', this.onOnline);
    this.stopConnectivityPolling();
    this.setOfflineUiMode(false);
    this.modalService.hide('network');
  }

  private startConnectivityPolling(): void {
    if (this.connectivityCheckIntervalId !== null) {
      return;
    }
    this.connectivityCheckIntervalId = this.win.setInterval(() => {
      this.syncConnectivityState();
    }, this.checkIntervalMs);
  }

  private stopConnectivityPolling(): void {
    if (this.connectivityCheckIntervalId === null) {
      return;
    }
    this.win.clearInterval(this.connectivityCheckIntervalId);
    this.connectivityCheckIntervalId = null;
  }

  private syncConnectivityState(): void {
    const isOnline = this.win.navigator.onLine;
    if (isOnline === this.lastKnownOnlineState) {
      return;
    }

    if (isOnline) {
      this.handleOnline();
      return;
    }
    this.handleOffline();
  }

  private handleOffline(): void {
    this.lastKnownOnlineState = false;
    this.setOfflineUiMode(true);
    if (this.offlineStartedAt === null) {
      this.offlineStartedAt = Date.now();
    }
    this.modalService.show('network');
  }

  private handleOnline(): void {
    this.lastKnownOnlineState = true;
    this.setOfflineUiMode(false);
    this.modalService.hide('network');
    if (this.offlineStartedAt !== null) {
      const startedAt = this.offlineStartedAt;
      this.offlineStartedAt = null;
      const durationMs = Date.now() - startedAt;
      this.newRelicEvents.trackEvent(NEW_RELIC_EVENT.KIOSK_CONNECTIVITY_RESTORED, {
        disconnection_duration: this.formatDisconnectionDurationDisplay(durationMs),
        offline_started_at: new Date(startedAt).toISOString(),
        offline_ended_at: new Date().toISOString(),
      });
    }
  }

  private setOfflineUiMode(isOffline: boolean): void {
    const body = this.win.document?.body;
    if (!body) {
      return;
    }
    body.classList.toggle(this.offlineBodyClass, isOffline);
  }

  private formatDisconnectionDurationDisplay(totalMs: number): string {
    const ms = Number.isFinite(totalMs) ? Math.max(0, Math.floor(totalMs)) : 0;
    let resto = ms;
    const horas = Math.floor(resto / 3_600_000);
    resto %= 3_600_000;
    const minutos = Math.floor(resto / 60_000);
    resto %= 60_000;
    const segundos = Math.floor(resto / 1_000);
    const milisegundos = resto % 1_000;

    const txtHoras = `${horas} ${horas === 1 ? 'hr' : 'hrs'}`;
    const txtMin = `${minutos} min`;
    const txtSeg = `${segundos} seg`;
    const txtMs = milisegundos === 1 ? '1 milisegundo' : `${milisegundos} milisegundos`;

    return `${txtHoras} ${txtMin} ${txtSeg} ${txtMs}`;
  }
}
